import type { RuleBuilderTacticParams } from '@/entities/pricing-rule'
import type {
	CatalogParamModel,
	CatalogPricingRuleModel,
} from '@/shared/generated'

import { useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	paramsDefaults,
	usePricingRuleCatalogQuery,
} from '@/entities/pricing-rule'

import {
	Field,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from '@/shared/components/ui/field'

import { ParamsForm } from './ParamsForm'
import { VariantSelect } from './VariantSelect'

/**
 * Tactics-tab controller for the rule-builder RB flow. Composes the variant
 * Select with the dynamically-generated params form, validates the params
 * against the selected variant's schema (a nested RHF form keyed by
 * `buildParamsSchema`), and persists `{ variant_id, params }` into the parent
 * campaign form at `name` (spec §3.3, §5).
 */
export const PricingTacticRuleFieldController = ({
	name,
	context,
}: {
	context?: {
		optimizationGroupId?: string
	}
	name: string
}) => {
	const { t } = useTranslation()
	const parent = useFormContext()
	const parentValue = useWatch({ control: parent.control, name }) as
		RuleBuilderTacticParams | undefined

	const ruleId = parentValue?.pricing_rule_id ?? ''
	const ruleVersionId = parentValue?.pricing_rule_version_id ?? ''

	const { data } = usePricingRuleCatalogQuery()
	const variants = data?.data ?? []
	const variant = variants.find((v) =>
		ruleVersionId
			? v.id === ruleId && v.version_id === ruleVersionId
			: v.id === ruleId
	)
	const buildRuleParamsData = useCallback(
		(nextVariant: CatalogPricingRuleModel) => {
			if (!variant?.params) return paramsDefaults(nextVariant.params)
			const currentParams = variant.params
			const currentValues = parentValue?.rule_params ?? {}

			const currentValuesHasMap = creteValuesHashMap(
				currentParams,
				currentValues
			)

			const nextParams = nextVariant.params
			const nextValues = paramsDefaults(nextVariant.params)

			const notChangedParams = nextParams.reduce(
				(acc, param) => {
					const currentValue = currentValuesHasMap[`${param.id}+${param.type}`]
					acc[param.id] = currentValue ?? nextValues[param.id]

					return acc
				},
				{} as Record<string, any>
			)

			return {
				...nextValues,
				...notChangedParams,
			}
		},
		[variant, parentValue]
	)

	const handleSelect = (nextRuleVersion: CatalogPricingRuleModel) => {
		// Replace `params` wholesale rather than setting keys individually: a body
		// seeded from an older schema default can carry a stale `rule_id`, and
		// leaking both id keys is rejected by the API.
		parent.setValue(
			name,
			{
				pricing_rule_id: nextRuleVersion.id,
				pricing_rule_version_id: nextRuleVersion.version_id ?? null,
				rule_params: buildRuleParamsData(nextRuleVersion),
			},
			{ shouldDirty: true }
		)
	}

	return (
		<FieldSet data-testid='pr-tactic'>
			<Field>
				<FieldLabel>
					{t('pr_tactic_rule', {
						defaultValue: 'Pricing rule',
					})}
				</FieldLabel>
				<VariantSelect
					ruleId={ruleId}
					ruleVersionId={ruleVersionId}
					onSelect={handleSelect}
				/>
				{variant?.description && (
					<p className='text-sm text-muted-foreground'>
						{t(variant.description, {
							defaultValue: variant.description,
						})}
					</p>
				)}
			</Field>
			<FieldGroup>
				<ParamsForm
					variant={variant}
					name={`${name}.rule_params`}
					context={context}
				/>
			</FieldGroup>
		</FieldSet>
	)
}

const creteValuesHashMap = (
	params: CatalogParamModel[],
	values: Record<string, any>
) =>
	params.reduce(
		(acc, param) => {
			acc[`${param.id}+${param.type}`] = values[param.id]

			return acc
		},
		{} as Record<string, any>
	)
